import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Send, Search, Trash2, ExternalLink, Heart, CalendarClock, Link as LinkIcon,
  Home, Sparkles, Loader2, Settings as SettingsIcon, Upload,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { MortgageCalculatorButton } from "../components/MortgageCalculator";

/**
 * MLS Search — the agent's search-and-share workstation.
 *
 * Layout is mobile-first:
 *  • Mobile: MLS iframe on top, Quick Share form stacked below.
 *  • Desktop (lg+): side-by-side — iframe on the left (primary),
 *    Quick Share panel on the right.
 *
 * The Quick Share panel lets the agent paste any listing URL — pulled from
 * the iframe (right-click → copy link), Zillow, Redfin, etc. — and drop it
 * straight into a client's portal Properties feed, with optional OG-tag
 * auto-enrichment (title, price hint, hero image).
 */
export default function Listings() {
  // ── MLS iframe URL from agent settings ────────────────────────────────
  const [mlsUrl, setMlsUrl] = useState("");
  const [mlsLoading, setMlsLoading] = useState(true);

  // ── Quick Share state ─────────────────────────────────────────────────
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [form, setForm] = useState({ url: "", title: "", price_hint: "", note: "", image_url: "", photos: [] });
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [enriching, setEnriching] = useState(false);
  const [autoFilled, setAutoFilled] = useState({ title: false, price_hint: false, image_url: false, photos: false });
  // Device-upload state — tracks the multi-file loop the label button
  // drives. `progress` becomes "3/5" while a batch is running so the
  // agent can see something is happening on slow mobile connections.
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [photoProgress, setPhotoProgress] = useState("");
  // Drag-and-drop reorder state — `dragIdx` is the thumb the user picked
  // up, `dragOverIdx` is the slot currently under the cursor. Both use
  // the unified `[image_url, ...photos]` index space so slot 0 is the hero.
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  /** Reorder photos across the unified `[hero, ...extras]` array. Dropping
   *  a thumbnail into slot 0 promotes it to the hero (which is what the
   *  portal card uses as slide 1 of the swipeable gallery). */
  const reorderPhoto = (from, to) => {
    if (from == null || to == null || from === to) return;
    const arr = [form.image_url, ...(form.photos || [])].filter(Boolean);
    if (from < 0 || from >= arr.length || to < 0 || to >= arr.length) return;
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    setForm(f => ({ ...f, image_url: arr[0] || "", photos: arr.slice(1) }));
    // Once the agent has manually rearranged, drop the "auto" badges so
    // they don't imply the current order came from OG scraping.
    setAutoFilled(a => ({ ...a, image_url: false, photos: false }));
  };

  /** Remove a photo at the unified index — slot 0 removal promotes the
   *  next photo to hero automatically, matching how the portal renders
   *  the gallery. */
  const removePhotoAt = (idx) => {
    const arr = [form.image_url, ...(form.photos || [])].filter(Boolean);
    if (idx < 0 || idx >= arr.length) return;
    arr.splice(idx, 1);
    setForm(f => ({ ...f, image_url: arr[0] || "", photos: arr.slice(1) }));
    setAutoFilled(a => ({ ...a, image_url: false, photos: false }));
  };

  /**
   * Fan out an <input type=file multiple> selection to the backend
   * upload endpoint. Each successful upload's signed URL is appended
   * to `form.photos` (or promoted to `form.image_url` when the hero
   * slot is still empty). Failures on individual files toast + skip;
   * the batch keeps going so one bad HEIC can't kill the whole flow.
   */
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target?.files || []);
    e.target.value = "";  // reset so the same file can be picked again
    if (!files.length) return;

    const heroFilled = !!form.image_url;
    const currentExtras = (form.photos || []).length;
    // Hard cap at 10 total (hero + extras).
    const capacity = 10 - (heroFilled ? 1 : 0) - currentExtras;
    if (capacity <= 0) {
      toast.error("Gallery is full — remove a photo to add more");
      return;
    }
    const batch = files.slice(0, capacity);
    if (files.length > capacity) {
      toast.message(`Uploading ${capacity} of ${files.length} — gallery caps at 10`);
    }

    setUploadingPhotos(true);
    let promotedHero = heroFilled;
    for (let i = 0; i < batch.length; i++) {
      const f = batch[i];
      setPhotoProgress(`${i + 1}/${batch.length}`);
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/listings/photo-upload", fd);
        if (!data?.url) throw new Error("no url");
        if (!promotedHero) {
          // First upload with an empty hero slot fills the hero so the
          // portal card has something to render even if the URL flow
          // never enriched.
          setForm(prev => ({ ...prev, image_url: data.url }));
          setAutoFilled(a => ({ ...a, image_url: false }));
          promotedHero = true;
        } else {
          setForm(prev => ({ ...prev, photos: [...(prev.photos || []), data.url].slice(0, 9) }));
          setAutoFilled(a => ({ ...a, photos: false }));
        }
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || "Upload failed";
        toast.error(`${f.name}: ${msg}`);
      }
    }
    setUploadingPhotos(false);
    setPhotoProgress("");
  };

  useEffect(() => {
    let cancelled = false;
    api.get("/auth/me")
      .then(({ data }) => { if (!cancelled) setMlsUrl(data?.mls_search_url || ""); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setMlsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    api.get("/clients").then(r => {
      setClients(r.data);
      if (r.data.length && !clientId) setClientId(r.data[0].id);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!clientId) { setRows([]); setLoading(false); return; }
    setLoading(true);
    api.get(`/listings/recommend?client_id=${clientId}`)
      .then(r => setRows(r.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [clientId]);

  const selectedClient = useMemo(
    () => clients.find(c => c.id === clientId),
    [clients, clientId],
  );

  // Auto-enrich once the URL looks complete. Debounced, and never overwrites
  // fields the agent already typed.
  useEffect(() => {
    const url = form.url.trim();
    if (!/^https?:\/\/\S+\.\S+/i.test(url)) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      setEnriching(true);
      try {
        const { data } = await api.post("/listings/preview", { url });
        if (cancelled || data.error) return;
        const patch = {};
        const flags = { ...autoFilled };
        if (data.title && !form.title.trim()) { patch.title = data.title; flags.title = true; }
        if (data.price_hint && !form.price_hint.trim()) { patch.price_hint = data.price_hint; flags.price_hint = true; }
        if (data.image_url && !form.image_url) { patch.image_url = data.image_url; flags.image_url = true; }
        // Additional photos beyond the hero — the portal renders these as a
        // swipeable gallery. Cap at 10 to match backend + PropertyGallery.
        if (Array.isArray(data.photos) && data.photos.length && (form.photos || []).length === 0) {
          const extras = data.photos
            .filter(u => u && u !== data.image_url && u !== form.image_url)
            .slice(0, 9);
          if (extras.length) { patch.photos = extras; flags.photos = true; }
        }
        if (Object.keys(patch).length) {
          setForm(f => ({ ...f, ...patch }));
          setAutoFilled(flags);
        }
      } catch (e) {
        console.debug("preview fetch failed", e?.response?.status || e?.message);
      } finally {
        if (!cancelled) setEnriching(false);
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.url]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!clientId || !form.url.trim()) {
      toast.error("Pick a client and paste a URL");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/listings/recommend", {
        url: form.url.trim(),
        client_id: clientId,
        title: form.title.trim() || null,
        price_hint: form.price_hint.trim() || null,
        note: form.note.trim() || null,
        image_url: form.image_url || null,
        photos: (form.photos || []).filter(Boolean).slice(0, 10),
      });
      setRows(prev => [{ ...data, client_id: clientId }, ...prev]);
      setForm({ url: "", title: "", price_hint: "", note: "", image_url: "", photos: [] });
      setAutoFilled({ title: false, price_hint: false, image_url: false, photos: false });
      toast.success(`Shared with ${selectedClient?.full_name || "client"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't share");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!confirm("Remove this recommendation? The client will no longer see it.")) return;
    try {
      await api.delete(`/listings/recommend/${id}`);
      setRows(prev => prev.filter(r => r.id !== id));
      toast.success("Removed");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-6">
      <header>
        <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Search &amp; Share</div>
        <div className="flex items-center justify-between gap-3">
          <h1 className="font-heading text-3xl font-bold tracking-tighter text-slate-900">MLS Search</h1>
          <MortgageCalculatorButton initialPrice={Number(form?.price) || 0} />
        </div>
        <p className="mt-1 text-sm text-slate-500 max-w-2xl">
          Browse the market from inside iKEY, then drop any URL into a client's Properties feed with one click.
        </p>
      </header>

      {/* Mobile-first: MLS on top, Quick Share stacked below.
          lg+ : side-by-side (MLS gets ~3/5, Quick Share ~2/5). */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-testid="mls-search-layout">
        <MlsPane mlsUrl={mlsUrl} loading={mlsLoading} />
        <QuickSharePane
          clients={clients}
          clientId={clientId}
          setClientId={setClientId}
          form={form}
          setForm={setForm}
          autoFilled={autoFilled}
          setAutoFilled={setAutoFilled}
          enriching={enriching}
          busy={busy}
          submit={submit}
          handlePhotoUpload={handlePhotoUpload}
          uploadingPhotos={uploadingPhotos}
          photoProgress={photoProgress}
          dragIdx={dragIdx}
          setDragIdx={setDragIdx}
          dragOverIdx={dragOverIdx}
          setDragOverIdx={setDragOverIdx}
          reorderPhoto={reorderPhoto}
          removePhotoAt={removePhotoAt}
        />
      </div>

      {/* Existing recommendations for the selected client */}
      <section data-testid="listings-history">
        <h2 className="font-heading font-bold text-lg tracking-tight text-slate-900">
          {selectedClient ? `Sent to ${selectedClient.full_name || selectedClient.email}` : "Recommendations"}
        </h2>
        {loading ? (
          <div className="mt-4 text-sm text-slate-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="mt-4 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-500" data-testid="listings-empty">
            <Home className="h-8 w-8 mx-auto text-slate-300 mb-2" />
            Nothing shared yet with this client.
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {rows.map(r => (
              <div
                key={r.id}
                className="rounded-lg border border-slate-200 bg-white p-4 flex flex-col gap-2"
                data-testid={`listing-${r.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{r.title || safeHost(r.url)}</div>
                    <div className="text-xs text-slate-500 truncate mt-0.5">{r.url}</div>
                    <div className="flex items-center gap-3 mt-2 text-xs">
                      {r.price_hint && <span className="font-semibold text-slate-900">{r.price_hint}</span>}
                      {r.liked_at && (
                        <span className="inline-flex items-center gap-1 text-rose-600 font-medium">
                          <Heart className="h-3 w-3 fill-rose-500" /> Liked
                        </span>
                      )}
                      {r.showing_requested_at && (
                        <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
                          <CalendarClock className="h-3 w-3" /> Showing requested
                        </span>
                      )}
                    </div>
                    {r.note && <p className="mt-2 text-xs text-slate-600 italic">"{r.note}"</p>}
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <a href={r.url} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded text-slate-500 hover:bg-slate-100" title="Open" data-testid={`listing-open-${r.id}`}>
                      <ExternalLink className="h-4 w-4" />
                    </a>
                    <button type="button" onClick={() => remove(r.id)} className="p-1.5 rounded text-slate-400 hover:bg-slate-100 hover:text-rose-600" title="Delete" data-testid={`listing-delete-${r.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// ── MLS iframe pane ─────────────────────────────────────────────────────
function MlsPane({ mlsUrl, loading }) {
  const iframeRef = useRef(null);
  // "unknown" until we've either had onLoad + a content check, or timed out
  const [status, setStatus] = useState("unknown"); // unknown | ok | blocked

  useEffect(() => {
    if (!mlsUrl) return;
    setStatus("unknown");
    // Safety-net timer — if we don't hear from onLoad in 6s, assume the
    // remote site blocked embedding via X-Frame-Options or CSP.
    const t = setTimeout(() => {
      setStatus(prev => (prev === "unknown" ? "blocked" : prev));
    }, 6000);
    return () => clearTimeout(t);
  }, [mlsUrl]);

  const handleLoad = () => {
    // Cross-origin content throws on `contentDocument` access — that's
    // actually the GOOD outcome (means the browser loaded a foreign page
    // successfully). Empty same-origin document = blocked.
    try {
      const doc = iframeRef.current?.contentDocument;
      if (doc && (!doc.body || doc.body.children.length === 0)) {
        setStatus("blocked");
        return;
      }
    } catch { /* cross-origin → content loaded fine */ }
    setStatus("ok");
  };

  if (loading) {
    return (
      <div className="lg:col-span-3 rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500" data-testid="mls-pane-loading">
        Loading MLS search…
      </div>
    );
  }
  if (!mlsUrl) {
    return (
      <div className="lg:col-span-3 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center" data-testid="mls-empty">
        <Search className="h-6 w-6 mx-auto text-slate-400 mb-2" />
        <h2 className="font-heading text-lg font-bold text-slate-900">No MLS search URL yet</h2>
        <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
          Paste your Zillow, Redfin, or MLS listing-search URL (with your default filters applied) in Settings.
          It shows up here and inside every client's portal instantly.
        </p>
        <Link
          to="/settings"
          className="mt-4 inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-semibold text-white"
          style={{ background: "var(--primary-hex)" }}
          data-testid="mls-go-to-settings"
        >
          <SettingsIcon className="h-4 w-4" /> Go to Settings
        </Link>
      </div>
    );
  }
  const blocked = status === "blocked";
  return (
    <div className="lg:col-span-3 rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm flex flex-col" data-testid="mls-pane">
      <div className="px-4 py-2.5 border-b border-slate-100">
        <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 inline-flex items-center gap-1.5">
          <Search className="h-3.5 w-3.5" /> Live MLS
        </div>
        <div className="mt-2 flex items-center justify-center gap-3 flex-wrap text-center" data-testid="mls-embed-note">
          <span className="text-xs font-bold text-red-600">
            Some sites block being embedded. If the box stays empty for a few seconds, use the open in new tab link.
          </span>
          <a
            href={mlsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-bold text-red-600 hover:text-red-700 inline-flex items-center gap-1 shrink-0"
            data-testid="mls-open-new-tab"
          >
            Open in new tab <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>
      <div className="relative" style={{ minHeight: 420, height: "72vh" }}>
        <iframe
          ref={iframeRef}
          src={mlsUrl}
          title="MLS Search"
          className="w-full h-full block"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
          onLoad={handleLoad}
          data-testid="mls-iframe"
        />
        {/* Blocked-embed fallback — high-contrast panel that overlays the
            iframe when X-Frame-Options / CSP prevents it from rendering.
            We keep the iframe mounted so if the site later stops blocking
            us the load event can update `status` back to ok. */}
        {blocked && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-50/95 backdrop-blur-sm p-6" data-testid="mls-blocked-fallback">
            <div className="max-w-sm text-center">
              <div className="mx-auto h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Search className="h-6 w-6 text-amber-800" />
              </div>
              <h3 className="mt-4 font-heading text-lg font-bold text-slate-900">This MLS site blocks embedding</h3>
              <p className="mt-1 text-sm text-slate-600">
                Their security settings won't let iKEY show the search inline. Open it in a new tab and copy any listing URL into Quick Share on the right — everything else still works exactly the same.
              </p>
              <a
                href={mlsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-semibold text-white shadow-sm"
                style={{ background: "var(--primary-hex)" }}
                data-testid="mls-launch-new-tab-btn"
              >
                <ExternalLink className="h-4 w-4" /> Launch MLS Search in New Tab
              </a>
              <div className="mt-3 text-[11px] text-slate-500 tabular-nums break-all">{mlsUrl}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Quick Share pane ────────────────────────────────────────────────────
function QuickSharePane({ clients, clientId, setClientId, form, setForm, autoFilled, setAutoFilled, enriching, busy, submit, handlePhotoUpload, uploadingPhotos, photoProgress, dragIdx, setDragIdx, dragOverIdx, setDragOverIdx, reorderPhoto, removePhotoAt }) {
  return (
    <form
      onSubmit={submit}
      className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-5"
      data-testid="quick-share-form"
    >
      <div className="flex items-center gap-2 mb-1">
        <Send className="h-4 w-4 text-slate-500" />
        <h2 className="font-heading font-bold text-lg tracking-tight text-slate-900">Quick share</h2>
      </div>
      <p className="text-xs text-slate-500 mb-4">Paste a URL from the MLS search — we'll auto-detect title, price and image.</p>

      <div className="grid grid-cols-1 gap-4">
        <div>
          <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Send to</label>
          <select
            value={clientId}
            onChange={e => setClientId(e.target.value)}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            data-testid="quick-share-client"
          >
            {clients.length === 0 && <option value="">No clients yet</option>}
            {clients.map(c => (
              <option key={c.id} value={c.id}>{c.full_name || c.email}</option>
            ))}
          </select>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Property URL</label>
            {enriching && (
              <span className="inline-flex items-center gap-1 text-[11px] text-slate-500" data-testid="enriching-indicator">
                <Loader2 className="h-3 w-3 animate-spin" /> Fetching preview…
              </span>
            )}
          </div>
          <div className="mt-1 relative">
            <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              required
              value={form.url}
              onChange={e => setForm({ ...form, url: e.target.value })}
              placeholder="https://www.zillow.com/homedetails/…"
              className="w-full rounded-md border border-slate-300 pl-9 pr-3 py-2 text-sm"
              data-testid="quick-share-url"
            />
          </div>
          {form.image_url && (
            <div className="mt-3 relative rounded-md overflow-hidden border border-slate-200 bg-slate-50" data-testid="preview-image-wrapper">
              <img src={form.image_url} alt="Preview" className="w-full max-h-40 object-cover" />
              {autoFilled.image_url && (
                <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-white/90 text-slate-800 text-[10px] font-semibold px-2 py-0.5 shadow ring-1 ring-slate-200">
                  <Sparkles className="h-3 w-3 text-amber-500" /> Auto-detected
                </span>
              )}
              <span className="absolute top-2 right-10 rounded-full bg-slate-900/85 text-white text-[10px] font-bold px-2 py-0.5 tracking-wide">HERO · SLIDE 1</span>
              <button
                type="button"
                onClick={() => { setForm(f => ({ ...f, image_url: (f.photos || [])[0] || "", photos: (f.photos || []).slice(1) })); setAutoFilled(a => ({ ...a, image_url: false })); }}
                className="absolute top-2 right-2 h-6 w-6 rounded-full bg-slate-900/70 text-white text-xs hover:bg-slate-900"
                title="Remove hero photo"
                data-testid="preview-image-clear"
              >×</button>
            </div>
          )}
          {/* Gallery — upload/paste/reorder photos, up to 10 total. Visible
              as soon as the URL field has content so agents can start
              uploading from their device before OG scraping even runs.
              Thumbnails support HTML5 native drag-to-reorder — drop a
              thumbnail into slot 1 to promote it to the hero. */}
          {form.url.trim() && (
            <div className="mt-3">
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">
                  Gallery <span className="text-slate-400">· drag to reorder · up to 10</span>
                  {autoFilled.photos && (
                    <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-semibold text-amber-600 normal-case tracking-normal" title="Auto-detected from the listing page">
                      <Sparkles className="h-3 w-3" /> auto
                    </span>
                  )}
                </label>
                <span className="text-[11px] text-slate-500 tabular-nums" data-testid="gallery-count">
                  {(form.image_url ? 1 : 0) + (form.photos || []).length} / 10
                </span>
              </div>
              {/* Unified reorderable strip — index 0 is the hero, rest are
                  extras. Dragging any thumb into any position rewrites both
                  `image_url` (slot 0) and `photos` in one setForm call so
                  the portal card renders the new order immediately after
                  Share. Uses HTML5 native drag-and-drop (no external libs). */}
              {((form.image_url ? 1 : 0) + (form.photos || []).length) > 0 && (
                <div className="flex gap-2 overflow-x-auto pb-1" data-testid="gallery-thumbs">
                  {[form.image_url, ...(form.photos || [])].filter(Boolean).map((src, idx) => (
                    <div
                      key={`${src}-${idx}`}
                      draggable
                      onDragStart={(e) => { setDragIdx(idx); try { e.dataTransfer.effectAllowed = "move"; } catch (err) { console.debug("dataTransfer effectAllowed failed", err); } }}
                      onDragOver={(e) => { e.preventDefault(); try { e.dataTransfer.dropEffect = "move"; } catch (err) { console.debug("dataTransfer dropEffect failed", err); } }}
                      onDragEnter={() => setDragOverIdx(idx)}
                      onDragLeave={() => setDragOverIdx(prev => prev === idx ? null : prev)}
                      onDrop={(e) => { e.preventDefault(); reorderPhoto(dragIdx, idx); setDragIdx(null); setDragOverIdx(null); }}
                      onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
                      className={`relative shrink-0 group cursor-grab active:cursor-grabbing transition-transform ${dragIdx === idx ? "opacity-40 scale-95" : ""} ${dragOverIdx === idx && dragIdx !== idx ? "ring-2 ring-offset-2 ring-pink-500" : ""}`}
                      data-testid={`gallery-thumb-${idx}`}
                      aria-label={`Photo ${idx + 1} of ${(form.image_url ? 1 : 0) + (form.photos || []).length}${idx === 0 ? ' — hero slide' : ''}. Drag to reorder.`}
                    >
                      <img src={src} alt={`Photo ${idx + 1}`} className="h-14 w-20 object-cover rounded border border-slate-200 pointer-events-none select-none" draggable="false" />
                      {idx === 0 && (
                        <span className="absolute inset-x-0 bottom-0 bg-slate-900/85 text-white text-[9px] font-bold text-center tracking-wider py-0.5 pointer-events-none rounded-b" data-testid="gallery-hero-badge">HERO</span>
                      )}
                      <button
                        type="button"
                        onClick={() => removePhotoAt(idx)}
                        className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-slate-900/80 text-white text-[11px] font-bold hover:bg-slate-900 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove"
                        aria-label={`Remove photo ${idx + 1}`}
                        data-testid={`gallery-remove-${idx}`}
                      >×</button>
                    </div>
                  ))}
                </div>
              )}
              {((form.image_url ? 1 : 0) + (form.photos || []).length) < 10 && (
                <div className="mt-1.5 flex flex-wrap gap-2 items-stretch">
                  <input
                    type="url"
                    placeholder="Paste another photo URL"
                    className="flex-1 min-w-[180px] rounded-md border border-slate-300 px-2.5 py-1.5 text-xs"
                    data-testid="gallery-add-input"
                    onKeyDown={(e) => {
                      if (e.key !== "Enter") return;
                      e.preventDefault();
                      const v = e.currentTarget.value.trim();
                      if (!/^https?:\/\//i.test(v)) return;
                      setForm(f => f.image_url
                        ? ({ ...f, photos: [...(f.photos || []), v].slice(0, 9) })
                        : ({ ...f, image_url: v })
                      );
                      setAutoFilled(a => ({ ...a, photos: false }));
                      e.currentTarget.value = "";
                    }}
                  />
                  <label
                    className={`shrink-0 inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer ${uploadingPhotos ? "opacity-60 pointer-events-none" : ""}`}
                    title="Upload photos from this device — HEIC files are auto-converted to JPEG"
                    data-testid="gallery-upload-btn"
                  >
                    {uploadingPhotos ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Uploading{photoProgress ? ` ${photoProgress}` : "…"}
                      </>
                    ) : (
                      <>
                        <Upload className="h-3.5 w-3.5" />
                        Upload from device
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/*,.heic,.heif"
                      multiple
                      className="hidden"
                      data-testid="gallery-upload-input"
                      disabled={uploadingPhotos}
                      onChange={handlePhotoUpload}
                    />
                  </label>
                </div>
              )}
            </div>
          )}
        </div>

        <div>
          <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">
            Title <span className="text-slate-400">· optional</span>
            {autoFilled.title && (
              <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-semibold text-amber-600 normal-case tracking-normal" title="Auto-detected — you can change it">
                <Sparkles className="h-3 w-3" /> auto
              </span>
            )}
          </label>
          <input
            value={form.title}
            onChange={e => { setForm({ ...form, title: e.target.value }); setAutoFilled(a => ({ ...a, title: false })); }}
            placeholder="Beautiful 3BR/2BA in Arcadia"
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            data-testid="quick-share-title"
          />
        </div>

        <div>
          <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">
            Price hint <span className="text-slate-400">· optional</span>
            {autoFilled.price_hint && (
              <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-semibold text-amber-600 normal-case tracking-normal" title="Auto-detected — you can change it">
                <Sparkles className="h-3 w-3" /> auto
              </span>
            )}
          </label>
          <input
            value={form.price_hint}
            onChange={e => { setForm({ ...form, price_hint: e.target.value }); setAutoFilled(a => ({ ...a, price_hint: false })); }}
            placeholder="$725,000"
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            data-testid="quick-share-price"
          />
        </div>

        <div>
          <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Note to your client <span className="text-slate-400">· optional</span></label>
          <textarea
            value={form.note}
            onChange={e => setForm({ ...form, note: e.target.value })}
            placeholder="Right in the school district you mentioned — take a look."
            rows={2}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            data-testid="quick-share-note"
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={busy || !form.url.trim() || !clientId}
        className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-md px-5 py-2.5 text-sm font-semibold text-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ background: "var(--primary-hex)" }}
        data-testid="quick-share-submit"
      >
        <Send className="h-4 w-4" /> {busy ? "Sending…" : "Share with client"}
      </button>
    </form>
  );
}

function safeHost(u) {
  try { return new URL(u).hostname; } catch { return u; }
}
